"use client";

import React from "react";
import { motion } from "framer-motion";

export interface TalktribeTechStackBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    layers?: Array<{
        tier: string;
        title: string;
        desc: string;
        tech: string[];
    }>;
}

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.7 },
};

export default function TalktribeTechStackBlock({
    tag = "Under the Hood",
    title = "Built With",
    description = "The technology stack powering TalkTribe's character engine and conversation memory.",
    layers = [
        { tier: "Frontend", title: "User Interface", desc: "A responsive web application with real-time chat streaming, character galleries, and seamless language switching across the entire platform.", tech: ["Next.js", "React", "Tailwind CSS", "SSE Streaming"] },
        { tier: "Backend", title: "AI & API Layer", desc: "FastAPI backend handling character prompt compilation, conversation routing, and real-time response generation with streaming output.", tech: ["FastAPI", "Python", "LLM Integration", "REST API"] },
        { tier: "Data", title: "Memory & Persistence", desc: "Vector database for semantic conversation memory, allowing characters to recall past interactions and maintain personality consistency.", tech: ["PostgreSQL", "pgvector", "Redis Cache", "Firebase Auth"] }
    ]
}: TalktribeTechStackBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#09090b] border-t border-zinc-900 w-full" data-header-theme="light">
            <div className="container mx-auto">
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-[11px] font-mono text-violet-400 uppercase tracking-widest block mb-4">// {tag}</span>
                    )}
                    <h2 className="text-3xl md:text-5xl font-light text-white leading-tight">{title}</h2>
                    {description && (
                        <p className="text-zinc-500 text-sm mt-2 font-light">{description}</p>
                    )}
                </div>

                <div className="space-y-4">
                    {layers.map((layer, i) => (
                        <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                            className="bg-[#0d0d10] border border-zinc-900 p-6 md:p-8 rounded-xl flex flex-col md:flex-row gap-6 items-start justify-between hover:border-zinc-800 transition-colors w-full">
                            <div className="space-y-2 md:max-w-xl">
                                <span className="text-[10px] font-mono text-violet-400 font-bold tracking-widest">{layer.tier.toUpperCase()}</span>
                                <h3 className="text-xl font-normal text-white">{layer.title}</h3>
                                <p className="text-zinc-400 text-xs md:text-sm leading-relaxed font-light">{layer.desc}</p>
                            </div>
                            <div className="flex flex-wrap gap-2 md:justify-end max-w-xs pt-4 md:pt-0">
                                {layer.tech.map((t, idx) => (
                                    <span key={idx} className="bg-zinc-950 border border-zinc-900 px-3 py-1 rounded text-[10px] font-mono text-zinc-400">{t}</span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
